/**
 * Local media for the Quick Editor and the workspace's Edit page, kept in this browser
 * (IndexedDB) so the editor opens media instantly and never downloads it twice.
 *
 * - Files added from disk, and media whose address only lives for this session, are
 *   kept under the asset's id. They stay when the asset is removed, because an undo, a
 *   pasted copy of the editor or a duplicated workspace may still use them. They are not
 *   synced: on another device the editor asks for the file again.
 * - Clips that live on the canvas (generated videos, Reference clips) are kept under a
 *   key for the file after their first download, so later visits read them from here.
 *   The oldest-used clips make room when the total passes a limit.
 * - Thumbnails are kept under 'thumb:' keys.
 *
 * The store is separate from the workspace database so large media never slows down
 * or complicates workspace saves.
 */

const DB_NAME = 'NoodleQuickEditorMedia';
const STORE = 'media';

/** Room for clips kept after a download; the oldest-used go first when a new clip does not fit. */
export const CLIP_CACHE_LIMIT_BYTES = 4 * 1024 * 1024 * 1024;

const CLIP_PREFIX = 'clip:';
const CLIP_META_PREFIX = 'clipmeta:';

interface ClipMeta {
    size: number;
    mimeType: string;
    usedAt: number;
}

const openDb = (): Promise<IDBDatabase> =>
    new Promise((resolve, reject) => {
        if (typeof indexedDB === 'undefined') {
            reject(new Error('Local storage for media is not available in this browser.'));
            return;
        }
        const request = indexedDB.open(DB_NAME, 1);
        request.onupgradeneeded = () => {
            if (!request.result.objectStoreNames.contains(STORE)) request.result.createObjectStore(STORE);
        };
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error ?? new Error('Could not open the local media store.'));
    });

const withDb = async <T,>(work: (db: IDBDatabase) => Promise<T>): Promise<T> => {
    const db = await openDb();
    try {
        return await work(db);
    } finally {
        db.close();
    }
};

/** Run requests in one transaction and resolve when it has committed. */
const transact = <T,>(db: IDBDatabase, mode: IDBTransactionMode, work: (store: IDBObjectStore) => T): Promise<T> =>
    new Promise<T>((resolve, reject) => {
        const transaction = db.transaction(STORE, mode);
        let result: T;
        try {
            result = work(transaction.objectStore(STORE));
        } catch (e) {
            transaction.abort();
            reject(e);
            return;
        }
        transaction.oncomplete = () => resolve(result);
        transaction.onerror = () => reject(transaction.error ?? new Error('Local media store failed.'));
        transaction.onabort = () => reject(transaction.error ?? new Error('Local media store ran out of space.'));
    });

const run = <T,>(mode: IDBTransactionMode, work: (store: IDBObjectStore) => IDBRequest<T>): Promise<T> =>
    withDb(async db => {
        const request = await transact(db, mode, work);
        return request.result;
    });

/** Keep a file under `key`. Rejects when the browser is out of space. */
export const putEditorMedia = (key: string, blob: Blob): Promise<void> =>
    run('readwrite', store => store.put(blob, key)).then(() => undefined);

/** The file stored under `key`, or null when this browser does not have it. */
export const getEditorMedia = async (key: string): Promise<Blob | null> => {
    const value = await run<unknown>('readonly', store => store.get(key));
    return value instanceof Blob ? value : null;
};

export const deleteEditorMedia = (key: string): Promise<void> =>
    run('readwrite', store => store.delete(key)).then(() => undefined);

const isClipMeta = (value: unknown): value is ClipMeta =>
    typeof value === 'object' && value !== null && typeof (value as ClipMeta).size === 'number';

/** A clip kept after an earlier download, or null. Reading it marks it as recently used. */
export const getCachedClip = (key: string): Promise<{ blob: Blob; mimeType: string } | null> =>
    withDb(async db => {
        const [blobRequest, metaRequest] = await transact(db, 'readonly', store => [
            store.get(CLIP_PREFIX + key),
            store.get(CLIP_META_PREFIX + key),
        ] as const);
        const blob = blobRequest.result;
        const meta = metaRequest.result;
        if (!(blob instanceof Blob) || !isClipMeta(meta)) return null;
        // Only the small record is rewritten, and only while the clip is still kept: an eviction
        // may have run since the read.
        await transact(db, 'readwrite', store => {
            const kept = store.count(CLIP_PREFIX + key);
            kept.onsuccess = () => {
                if (kept.result > 0) store.put({ ...meta, usedAt: Date.now() }, CLIP_META_PREFIX + key);
            };
        }).catch(() => undefined);
        return { blob, mimeType: meta.mimeType || blob.type };
    });

/**
 * Keep a clip under `key`, then drop the least recently used clips until the total fits
 * within `limitBytes`. A clip larger than the whole limit is not kept at all, so one big
 * file can never empty the store. Rejects when the browser is out of space.
 */
export const putCachedClip = async (key: string, blob: Blob, mimeType: string, limitBytes: number = CLIP_CACHE_LIMIT_BYTES): Promise<void> => {
    if (blob.size === 0 || blob.size > limitBytes) return;
    await withDb(async db => {
        const write = () => transact(db, 'readwrite', store => {
            store.put(blob, CLIP_PREFIX + key);
            store.put({ size: blob.size, mimeType: mimeType || blob.type, usedAt: Date.now() } satisfies ClipMeta, CLIP_META_PREFIX + key);
        });
        try {
            await write();
        } catch (e) {
            if ((e as { name?: string })?.name !== 'QuotaExceededError') throw e;
            // The browser has less room than the limit allows. Drop the oldest clips to make room, once,
            // and only when that can make enough room: the store is never emptied for nothing.
            const estimate = typeof navigator !== 'undefined' ? await navigator.storage?.estimate?.().catch(() => null) : null;
            const free = estimate?.quota != null && estimate.usage != null ? Math.max(0, estimate.quota - estimate.usage) : 0;
            const needed = blob.size - free;
            let enough = false;
            await evictClips(db, key, entries => {
                const total = entries.reduce((sum, entry) => sum + entry.size, 0);
                enough = total >= needed;
                return enough ? Math.max(0, total - needed) : total;
            });
            if (!enough) throw e;
            await write();
        }
        await evictClips(db, key, () => limitBytes);
    });
};

/** Whether a clip is kept under `key`, without reading it or marking it used. */
export const hasCachedClip = (key: string): Promise<boolean> =>
    withDb(async db => (await transact(db, 'readonly', store => store.count(CLIP_PREFIX + key))).result > 0);

export const deleteCachedClip = (key: string): Promise<void> =>
    withDb(db => transact(db, 'readwrite', store => {
        store.delete(CLIP_PREFIX + key);
        store.delete(CLIP_META_PREFIX + key);
    }));

export interface KeptClipEntry {
    key: string;
    size: number;
    usedAt: number;
}

/** Which kept clips to drop, least recently used first and never `keep`, so the rest fit within `limitBytes`. */
export const clipsToEvict = (entries: KeptClipEntry[], limitBytes: number, keep: string): string[] => {
    let total = entries.reduce((sum, entry) => sum + entry.size, 0);
    const evicted: string[] = [];
    for (const entry of [...entries].sort((a, b) => a.usedAt - b.usedAt)) {
        if (total <= limitBytes) break;
        if (entry.key === keep) continue;
        evicted.push(entry.key);
        total -= entry.size;
    }
    return evicted;
};

/**
 * Drop the least recently used clips, never `keep`, until the kept clips fit within the
 * size `target` gives for them. Reading and dropping happen in one transaction.
 */
const evictClips = (db: IDBDatabase, keep: string, target: (entries: KeptClipEntry[]) => number): Promise<void> =>
    new Promise<void>((resolve, reject) => {
        const transaction = db.transaction(STORE, 'readwrite');
        const store = transaction.objectStore(STORE);
        const entries: KeptClipEntry[] = [];
        const cursorRequest = store.openCursor(IDBKeyRange.bound(CLIP_META_PREFIX, `${CLIP_META_PREFIX}\uffff`));
        cursorRequest.onsuccess = () => {
            const cursor = cursorRequest.result;
            if (cursor) {
                if (isClipMeta(cursor.value)) {
                    entries.push({ key: String(cursor.key).slice(CLIP_META_PREFIX.length), size: cursor.value.size, usedAt: cursor.value.usedAt });
                }
                cursor.continue();
                return;
            }
            for (const key of clipsToEvict(entries, target(entries), keep)) {
                store.delete(CLIP_PREFIX + key);
                store.delete(CLIP_META_PREFIX + key);
            }
        };
        transaction.oncomplete = () => resolve();
        transaction.onerror = () => reject(transaction.error ?? new Error('Local media store failed.'));
        transaction.onabort = () => reject(transaction.error ?? new Error('Local media store failed.'));
    });
